import { useCallback, useMemo, useState } from 'react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import Select, { InputActionMeta, SingleValue } from 'react-select';

import { IconButton } from '@/components/IconButton';
import { buildSelectStyles, SELECT_THEMES } from '@/components/filters/selectStyles';
import { SelectOption, toOptions, toValue, toValueFromOptions } from '@/components/filters/selectUtils';
import type { ForecastFilters } from '@/features/forecasts/components/explorer/filters/ForecastFiltersUrl';
import { BFG_MODEL_CONFIG } from '@/features/forecasts/config/fctRuns';
import type { ForecastFacets } from '@/features/forecasts/hooks/useForecastFacets';
import { availableTargetWeeks, fctsForWindow } from '@/features/forecasts/utils/forecastWindow';
import { useCustomers } from '@/hooks/data/useCustomers';
import { useProductSkus } from '@/hooks/data/useProductSkus';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cls } from '@/utils/cls';
import { formatIsoDateLabel } from '@/utils/date';
import { format } from '@/utils/format';
import { uniqueSortedStrings } from '@/utils/sort';

type FiltersDrawerProps = {
  draft: ForecastFilters;
  facets: ForecastFacets;
  open: boolean;
  onApply: () => void;
  onClear: () => void;
  onClearSearch?: () => void;
  onClose: () => void;
  searchQuery?: string;
  setDraft: (next: ForecastFilters) => void;
};

function toOptionsFromFacet(options: SelectOption[]): SelectOption[] {
  return (options || []).map((option) => ({ value: option.value, label: option.label }));
}

export function ForecastFiltersDrawer({
  draft,
  facets,
  open,
  onApply,
  onClear,
  onClearSearch,
  onClose,
  searchQuery,
  setDraft,
}: FiltersDrawerProps) {
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [productSearchInput, setProductSearchInput] = useState('');
  const [selectedCustomerLabelsByValue, setSelectedCustomerLabelsByValue] = useState<Record<string, string>>({});
  const [selectedProductLabelsByValue, setSelectedProductLabelsByValue] = useState<Record<string, string>>({});
  const debouncedCustomerSearchInput = useDebouncedValue(customerSearchInput.trim(), 350);
  const debouncedProductSearchInput = useDebouncedValue(productSearchInput.trim(), 350);
  const { customers, isFetching: isCustomerOptionsLoading } = useCustomers({
    page: 1,
    pageSize: 100,
    query: debouncedCustomerSearchInput,
    sortDir: 'asc',
    sortKey: 'name',
  });
  const { isFetching: isProductOptionsLoading, skus } = useProductSkus({
    page: 1,
    pageSize: 100,
    query: debouncedProductSearchInput,
    selections: {},
    sortDir: 'asc',
    sortKey: 'productId',
  });
  const hasSearch = Boolean(searchQuery && searchQuery.trim().length > 0);
  const canClearSearch = hasSearch && typeof onClearSearch === 'function';
  const styles = buildSelectStyles(SELECT_THEMES.forecast);
  const noOptionsMessage = () => 'No options found';
  const selectedLeadTime = draft.leadTime || '';
  const selectedSpan = draft.span || '';
  const forecastStart = draft.forecastStart || '';
  const forecastEnd = draft.forecastEnd || '';

  // The FCT universe for both the window options and the derivation must be the
  // same list, or applying a window can derive an empty/stale fcts set. Facets
  // load lazily from the API (and only when the drawer is open), and the page is
  // seeded from DEFAULT_FCTS, so neither alone is authoritative — union them.
  // draft.fcts holds the currently-applied FCTs (seeded or previously derived).
  const fctOptions = useMemo(
    () => uniqueSortedStrings([...facets.forecastCreationTimes, ...(draft.fcts ?? [])]),
    [facets.forecastCreationTimes, draft.fcts],
  );

  // Users pick a forecast window (the target weeks they want to see); the
  // concrete FCTs are derived from that window + lead time. Only target weeks
  // that map back to a real FCT are offered.
  const targetWeekOptions = useMemo(
    () => availableTargetWeeks(fctOptions, selectedLeadTime),
    [fctOptions, selectedLeadTime],
  );
  // Always keep the currently-selected window endpoints selectable, even if the
  // facet-derived options haven't loaded yet (or were seeded from a default FCT
  // list that the facets don't list verbatim). The stored value is valid by
  // construction — it was derived from real FCTs — so a transient empty facet
  // set must not silently blank the default the user navigated in with.
  const selectableWeeks = useMemo(
    () => uniqueSortedStrings([...targetWeekOptions, forecastStart, forecastEnd].filter(Boolean)),
    [targetWeekOptions, forecastStart, forecastEnd],
  );
  const effectiveStart = forecastStart;
  const effectiveEnd = forecastEnd;
  const startOptions = useMemo(
    () => (effectiveEnd ? selectableWeeks.filter((d) => d <= effectiveEnd) : selectableWeeks),
    [selectableWeeks, effectiveEnd],
  );
  const endOptions = useMemo(
    () => (effectiveStart ? selectableWeeks.filter((d) => d >= effectiveStart) : selectableWeeks),
    [selectableWeeks, effectiveStart],
  );

  // Re-derive fcts from the window whenever the window or lead time changes.
  function applyWindow(next: ForecastFilters): ForecastFilters {
    const fcts = fctsForWindow(fctOptions, next.leadTime || '', next.forecastStart, next.forecastEnd);
    return { ...next, fcts: fcts.length ? fcts : undefined };
  }
  // Lead time / span options come from BFG's leadtime-span pairs (the source-of-truth model).
  const modelPairs = useMemo(
    () => facets.leadtimeSpansByModel[BFG_MODEL_CONFIG.modelName] ?? facets.leadtimeSpans,
    [facets.leadtimeSpans, facets.leadtimeSpansByModel],
  );
  const leadTimeOptions = useMemo(
    () =>
      uniqueSortedStrings(
        (selectedSpan ? modelPairs.filter(([, s]) => String(s) === selectedSpan) : modelPairs).map(([lt]) =>
          String(lt),
        ),
      ),
    [modelPairs, selectedSpan],
  );
  const spanOptions = useMemo(
    () =>
      uniqueSortedStrings(
        (selectedLeadTime ? modelPairs.filter(([lt]) => String(lt) === selectedLeadTime) : modelPairs).map(([, s]) =>
          String(s),
        ),
      ),
    [modelPairs, selectedLeadTime],
  );
  const effectiveLeadTime = leadTimeOptions.includes(selectedLeadTime) ? selectedLeadTime : '';
  const effectiveSpan = spanOptions.includes(selectedSpan) ? selectedSpan : '';
  const customerOptions = useMemo(() => {
    if (customers.length === 0) return facets.customers;
    return customers.map((c) => ({ label: `${c.customerId} - ${c.name || c.customerId}`, value: c.customerId }));
  }, [customers, facets.customers]);
  const productOptions = useMemo(() => {
    if (skus.length === 0) return facets.products;
    return skus.map((sku) => ({
      label: `${sku.productId} - ${sku.productDescription || sku.productId}`,
      value: sku.productId,
    }));
  }, [facets.products, skus]);
  const customerLabelsByValue = useMemo(() => {
    const fromOptions = customerOptions.reduce<Record<string, string>>((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
    return { ...selectedCustomerLabelsByValue, ...fromOptions };
  }, [customerOptions, selectedCustomerLabelsByValue]);
  const productLabelsByValue = useMemo(() => {
    const fromOptions = productOptions.reduce<Record<string, string>>((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
    return { ...selectedProductLabelsByValue, ...fromOptions };
  }, [productOptions, selectedProductLabelsByValue]);

  const handleProductInputChange = useCallback((value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setProductSearchInput(value);
      return;
    }
    if (actionMeta.action === 'set-value') setProductSearchInput('');
  }, []);

  const handleCustomerInputChange = useCallback((value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') setCustomerSearchInput(value);
    if (actionMeta.action === 'set-value') setCustomerSearchInput('');
  }, []);

  const handleCustomerChange = useCallback(
    (opt: SingleValue<SelectOption>) => {
      const next = opt?.value;
      setDraft({ ...draft, customer: next });
      if (opt) setSelectedCustomerLabelsByValue((previous) => ({ ...previous, [opt.value]: opt.label }));
      setCustomerSearchInput('');
    },
    [draft, setDraft],
  );

  const handleProductChange = useCallback(
    (opt: SingleValue<SelectOption>) => {
      const next = opt?.value;
      setDraft({ ...draft, product: next });
      if (opt) setSelectedProductLabelsByValue((previous) => ({ ...previous, [opt.value]: opt.label }));
      setProductSearchInput('');
    },
    [draft, setDraft],
  );

  return (
    <div className={cls('fixed inset-0 z-[1000]', open ? 'pointer-events-auto' : 'pointer-events-none')}>
      <div
        className={cls('absolute inset-0 bg-black/30 transition-opacity', open ? 'opacity-100' : 'opacity-0')}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      />
      <div
        className={cls(
          'absolute right-0 top-0 h-full w-90 bg-white shadow-xl transition-transform',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={'Filters'}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">{'Filters'}</h2>
              {canClearSearch ? (
                <div
                  className="inline-flex max-w-50 items-center gap-1 rounded-full bg-[#274483]/5 px-2 py-0.5 text-xs font-semibold text-[#274483]"
                  title={format('Search: {{value}}', { value: searchQuery })}
                >
                  <span className="max-w-35 truncate">{format('Search: {{value}}', { value: searchQuery })}</span>
                  <button
                    type="button"
                    onClick={onClearSearch}
                    className="rounded p-0.5 text-[#274483] hover:bg-[#274483]/10"
                    aria-label={'Clear search'}
                  >
                    ×
                  </button>
                </div>
              ) : null}
            </div>
            <IconButton onClick={onClose} aria-label={'Close filters'} className="p-2 cursor-pointer">
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </IconButton>
          </div>

          <div className="flex-1 overflow-auto px-4 py-4 space-y-6">
            <section className="space-y-3">
              <div className="text-sm font-semibold text-slate-900">{'Narrow by'}</div>
              <div className="space-y-2">
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">{'Product'}</div>
                  <Select<SelectOption, false>
                    inputId="forecast-product-select"
                    styles={styles}
                    isClearable
                    inputValue={productSearchInput}
                    isLoading={isProductOptionsLoading}
                    options={toOptionsFromFacet(productOptions)}
                    noOptionsMessage={noOptionsMessage}
                    placeholder={'Type to search products'}
                    value={toValueFromOptions(productOptions, draft.product, productLabelsByValue)}
                    onInputChange={handleProductInputChange}
                    onChange={handleProductChange}
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">{'Customer'}</div>
                  <Select<SelectOption, false>
                    inputId="forecast-customer-select"
                    styles={styles}
                    isClearable
                    inputValue={customerSearchInput}
                    isLoading={isCustomerOptionsLoading}
                    options={toOptionsFromFacet(customerOptions)}
                    noOptionsMessage={noOptionsMessage}
                    placeholder={'Type to search customers'}
                    value={toValueFromOptions(customerOptions, draft.customer, customerLabelsByValue)}
                    onInputChange={handleCustomerInputChange}
                    onChange={handleCustomerChange}
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">Forecast start</div>
                  <Select<SelectOption, false>
                    inputId="forecast-start-select"
                    styles={styles}
                    isClearable
                    placeholder={selectedLeadTime ? 'Earliest target week' : 'All lead times'}
                    isDisabled={!selectedLeadTime}
                    options={startOptions.map((d) => ({ value: d, label: formatIsoDateLabel(d) }))}
                    noOptionsMessage={noOptionsMessage}
                    value={effectiveStart ? { value: effectiveStart, label: formatIsoDateLabel(effectiveStart) } : null}
                    onChange={(opt: SingleValue<SelectOption>) =>
                      setDraft(applyWindow({ ...draft, forecastStart: opt?.value }))
                    }
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">Forecast end</div>
                  <Select<SelectOption, false>
                    inputId="forecast-end-select"
                    styles={styles}
                    isClearable
                    placeholder={selectedLeadTime ? 'Latest target week' : 'All lead times'}
                    isDisabled={!selectedLeadTime}
                    options={endOptions.map((d) => ({ value: d, label: formatIsoDateLabel(d) }))}
                    noOptionsMessage={noOptionsMessage}
                    value={effectiveEnd ? { value: effectiveEnd, label: formatIsoDateLabel(effectiveEnd) } : null}
                    onChange={(opt: SingleValue<SelectOption>) =>
                      setDraft(applyWindow({ ...draft, forecastEnd: opt?.value }))
                    }
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">{'Lead time'}</div>
                  <Select<SelectOption, false>
                    inputId="forecast-lead-time-select"
                    styles={styles}
                    isClearable
                    placeholder={'All lead times'}
                    options={toOptions(leadTimeOptions)}
                    noOptionsMessage={noOptionsMessage}
                    value={toValue(effectiveLeadTime || undefined)}
                    onChange={(opt: SingleValue<SelectOption>) => {
                      const next = opt?.value;
                      const validSpans = next
                        ? uniqueSortedStrings(
                            modelPairs.filter(([lt]) => String(lt) === next).map(([, s]) => String(s)),
                          )
                        : spanOptions;
                      // Lead time shifts the FCT↔target-week mapping, so the
                      // window's reachable weeks change — keep start/end only if
                      // still reachable, then re-derive fcts.
                      const reachable = availableTargetWeeks(fctOptions, next || '');
                      setDraft(
                        applyWindow({
                          ...draft,
                          leadTime: next,
                          span: validSpans.includes(selectedSpan) ? selectedSpan : undefined,
                          forecastStart: reachable.includes(forecastStart) ? forecastStart : undefined,
                          forecastEnd: reachable.includes(forecastEnd) ? forecastEnd : undefined,
                        }),
                      );
                    }}
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-700">{'Span'}</div>
                  <Select<SelectOption, false>
                    inputId="forecast-span-select"
                    styles={styles}
                    isClearable
                    placeholder={'All spans'}
                    options={toOptions(spanOptions)}
                    noOptionsMessage={noOptionsMessage}
                    value={toValue(effectiveSpan || undefined)}
                    onChange={(opt: SingleValue<SelectOption>) => {
                      const next = opt?.value;
                      const validLeadTimes = next
                        ? uniqueSortedStrings(
                            modelPairs.filter(([, s]) => String(s) === next).map(([lt]) => String(lt)),
                          )
                        : leadTimeOptions;
                      setDraft({
                        ...draft,
                        span: next,
                        leadTime: validLeadTimes.includes(selectedLeadTime) ? selectedLeadTime : undefined,
                      });
                    }}
                  />
                </div>
              </div>
            </section>
          </div>

          <div className="border-t border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onClear}
                className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                {'Clear'}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  {'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={onApply}
                  className="rounded-md bg-[#274483] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1e3566]"
                >
                  {'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
